/**
 * AI Fit Block Component
 * 
 * Shows FREE users a static preview of AI Fit
 * Shows Pack Leader users personalized AI Fit analysis
 * 
 * CRITICAL: Compact variant on hero cards for FREE preview + Pack Leader.
 * Compact on carousel cards for Pack Leader only (isHero === false).
 * 
 * ELIGIBILITY:
 * - FREE: matchScore >= 70 AND category is fit-relevant AND isHero === true
 * - Pack Leader: matchScore >= 80 AND category is fit-relevant AND dog has signals AND isHero === true
 */

'use client'

import React, { useCallback, useState } from 'react'
import { ChevronRight, ChevronUp, Lock, Sparkles } from 'lucide-react'
import { useUpgradeModal } from '@/lib/monetization/useUpgradeModal'
import { useAiConsentGateOptional } from '@/components/compliance/AiConsentProvider'
import {
  isPicksV2Visual,
  picksV2CtaPrimary,
  picksV2InfoPanelStyle,
  picksV2NestedPlaceCardStyle,
  picksV2Type,
} from '@/lib/picks/picksV2Visual'

/** Free SKU benefits copy — never mixed with analysis/error states. */
export const AI_FIT_SKU_BENEFITS_COPY =
  'Unlock personalized AI Fit for this product'

/** Pack Leader invitation (not an error). */
export const AI_FIT_SKU_OPEN_ANALYSIS_COPY =
  'See why this product matches your dog'

/** Single concise failure — no marketing/limit copy. */
export const AI_FIT_SKU_ERROR_COPY =
  'Couldn’t load this product’s AI Fit. Try again.'

function isPersonalizedReason(reason?: string | null): boolean {
  if (typeof reason !== 'string') return false
  const trimmed = reason.trim()
  if (!trimmed) return false
  const lower = trimmed.toLowerCase()
  if (lower === 'recommended for your dog') return false
  if (lower.includes('fallback scoring')) return false
  return true
}

interface AIFitBlockProps {
  isPackLeader: boolean
  isHero: boolean
  /** SKU carousel card — Free get upgrade entry; Pack Leader get per-SKU analysis. */
  isSkuCard?: boolean
  dogName?: string
  matchScore?: number | null
  suitabilityReason?: string | null
  analysisUnavailable?: boolean
  /** Server coverage limit — not shown in marketing copy. */
  aiProductLimit?: number
  category?: string | null
  subcategory?: string | null
  bundleTitle?: string | null
  /** Required for on-demand Pack Leader SKU analysis. */
  productId?: string
  productTitle?: string
  affiliateUrl?: string
  provider?: string
  requestDogId?: string | null
  dogProfile?: {
    id?: string
    name?: string
    size?: string
    ageYears?: number
    ageBracket?: string
    breed?: string
  } | null
  theme: {
    textColor: string
    cardBackground: string
    cardBorder: string
    visualVariant?: 'v2'
  }
  className?: string
  variant?: 'full' | 'compact'
}

/**
 * Check if category is fit-relevant
 * For hero cards, also checks bundleTitle (bundle ID like "walk_essentials", "health_wellness")
 */
function isFitRelevantCategory(category?: string | null, subcategory?: string | null, bundleTitle?: string | null): boolean {
  const catLower = (category || '').toLowerCase()
  const subcatLower = (subcategory || '').toLowerCase()
  const bundleLower = (bundleTitle || '').toLowerCase()
  const combined = `${catLower} ${subcatLower} ${bundleLower}`.trim()
  
  // Fit-relevant bundle IDs (for hero cards)
  const fitRelevantBundles = [
    'walk_essentials',
    'health_wellness',
  ]
  
  // Check if bundleTitle is a fit-relevant bundle
  // CRITICAL: For hero cards, bundleTitle should be the pillId (e.g., "walk_essentials", "health_wellness")
  if (bundleLower && fitRelevantBundles.some(bundle => {
    const matches = bundleLower.includes(bundle) || bundle.includes(bundleLower)
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development' && matches) {
      console.log('[AIFitBlock] ✅ Bundle matches fit-relevant:', { bundleLower, bundle, matches })
    }
    return matches
  })) {
    return true
  }
  
  // Fit-relevant categories (check both category and subcategory)
  const fitRelevant = [
    'harness',
    'leash',
    'lead',
    'collar',
    'id tag',
    'tag',
    'training',
    'tech collar',
    'service',
    'vest',
    'working',
    'health',
    'wellness',
    'gear',
    'hip',
    'joint',
    'calming',
    'skin',
    'coat',
    'vitamin',
    'supplement',
    'flea',
    'tick',
    'prevention',
  ]
  
  // Exclude non-fit categories
  const exclude = ['toy', 'treat', 'food', 'bed', 'furniture', 'crate', 'kennel']
  
  // Check if category or subcategory contains fit-relevant terms
  const isRelevant = fitRelevant.some(term => 
    catLower.includes(term) || subcatLower.includes(term)
  )
  const isExcluded = exclude.some(term => 
    catLower.includes(term) || subcatLower.includes(term)
  )
  
  return isRelevant && !isExcluded
}

/**
 * Check if dog profile has required signals
 */
function hasRequiredDogSignals(dogProfile?: {
  size?: string
  ageYears?: number
  ageBracket?: string
  breed?: string
} | null): boolean {
  if (!dogProfile) return false
  
  // At least one of: size, age, or breed
  return !!(dogProfile.size || dogProfile.ageYears || dogProfile.ageBracket || dogProfile.breed)
}

/**
 * Get confidence tier label (no numeric scores)
 */
function getConfidenceTier(matchScore: number): string | null {
  if (matchScore >= 90 && matchScore < 100) {
    return 'Excellent Fit'
  } else if (matchScore >= 80 && matchScore < 90) {
    return 'Strong Fit'
  }
  return null
}

/**
 * Get FREE tier static copy by category/bundle
 */
function getFreeTierCopy(category?: string | null, subcategory?: string | null, bundleTitle?: string | null): string {
  const catLower = (category || '').toLowerCase()
  const subcatLower = (subcategory || '').toLowerCase()
  const bundleLower = (bundleTitle || '').toLowerCase()
  const combined = `${catLower} ${subcatLower} ${bundleLower}`.trim()
  
  // Harnesses / Leashes / Collars
  if (combined.includes('harness') || combined.includes('leash') || combined.includes('lead') || combined.includes('collar')) {
    return 'Designed for comfort and stability during everyday walks.'
  }
  
  // Hip & Joint / Health (check subcategory first for specificity, then category)
  if (subcatLower.includes('hip') || subcatLower.includes('joint') || 
      catLower.includes('vitamin') || catLower.includes('supplement') ||
      combined.includes('hip') || combined.includes('joint') || 
      combined.includes('health') || combined.includes('wellness')) {
    return 'Supports comfort during typical daily movement.'
  }
  
  // Calming
  if (combined.includes('calming') || combined.includes('anxiety')) {
    return 'Helps promote a calmer experience in stimulating environments.'
  }
  
  // Skin & Coat
  if (combined.includes('skin') || combined.includes('coat')) {
    return 'Supports coat health and skin comfort.'
  }
  
  // ID Tags
  if (combined.includes('id tag') || (combined.includes('tag') && !combined.includes('collar'))) {
    return 'Lightweight and practical for everyday identification.'
  }
  
  // Service Dog Vests
  if (combined.includes('service') || combined.includes('vest') || combined.includes('working')) {
    return 'Designed for visibility and extended wear.'
  }
  
  // Training & Tech Collars
  if (combined.includes('training') || combined.includes('tech collar')) {
    return 'Built for structured training and guided walks.'
  }
  
  // Default fallback
  return 'Suitable for everyday walking and daily use.'
}

export function AIFitBlock({
  isPackLeader,
  isHero,
  isSkuCard = false,
  dogName,
  matchScore,
  suitabilityReason,
  analysisUnavailable = false,
  aiProductLimit: _aiProductLimit = 3,
  category,
  subcategory,
  bundleTitle,
  productId,
  productTitle,
  affiliateUrl,
  provider,
  requestDogId,
  dogProfile,
  theme,
  className = '',
  variant = 'full',
}: AIFitBlockProps) {
  const { openUpgradeModal, ctaPending } = useUpgradeModal()
  const consentGate = useAiConsentGateOptional()
  const isV2 = isPicksV2Visual(theme)
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [liveReason, setLiveReason] = useState<string | null>(null)
  const [liveScore, setLiveScore] = useState<number | null>(null)

  const propPersonalized =
    isPackLeader &&
    !analysisUnavailable &&
    isPersonalizedReason(suitabilityReason)

  const effectiveReason =
    (liveReason && isPersonalizedReason(liveReason) ? liveReason : null) ||
    (propPersonalized ? String(suitabilityReason).trim() : null)

  const effectiveScore =
    liveScore != null
      ? liveScore
      : propPersonalized && matchScore != null
        ? matchScore
        : null

  const fetchSkuAnalysis = useCallback(async () => {
    if (!productId) {
      setError(AI_FIT_SKU_ERROR_COPY)
      return
    }
    setLoading(true)
    setError(null)
    try {
      if (consentGate) {
        const ok = await consentGate.ensureConsent()
        if (!ok) {
          setLoading(false)
          return
        }
      }
      const dogId =
        requestDogId ||
        dogProfile?.id ||
        null
      const res = await fetch('/api/agentic-picks', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          limit: 1,
          requestDogId: dogId,
          dogProfile: dogProfile
            ? {
                id: dogProfile.id,
                name: dogProfile.name || dogName,
                breed: dogProfile.breed,
                size: dogProfile.size,
                ageYears: dogProfile.ageYears,
                ageBracket: dogProfile.ageBracket,
              }
            : dogName
              ? { name: dogName }
              : undefined,
          products: [
            {
              id: productId,
              title: productTitle || 'Product',
              name: productTitle || 'Product',
              category: category || undefined,
              subcategory: subcategory || undefined,
              affiliateUrl: affiliateUrl || '#',
              provider: provider || 'chewy',
            },
          ],
        }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        enhancedProducts?: Array<{
          id?: string
          matchScore?: number
          suitabilityReason?: string
        }>
        analysisUnavailable?: boolean
        error?: string
        reason?: string
      }
      if (res.status === 499) {
        setLoading(false)
        return
      }
      if (res.status === 403 && data.error) {
        setError(AI_FIT_SKU_ERROR_COPY)
        setLoading(false)
        return
      }
      const row = Array.isArray(data.enhancedProducts)
        ? data.enhancedProducts.find(
            (p) => String(p.id || '').trim() === String(productId).trim()
          ) || data.enhancedProducts[0]
        : null
      const reason = row?.suitabilityReason
      if (
        row &&
        isPersonalizedReason(reason) &&
        String(row.id || '').trim() === String(productId).trim()
      ) {
        setLiveReason(String(reason).trim())
        setLiveScore(
          typeof row.matchScore === 'number' ? row.matchScore : null
        )
        setError(null)
      } else {
        // Honest failure — no generic filler, no parent inheritance
        setLiveReason(null)
        setLiveScore(null)
        setError(AI_FIT_SKU_ERROR_COPY)
      }
    } catch {
      setError(AI_FIT_SKU_ERROR_COPY)
    } finally {
      setLoading(false)
    }
  }, [
    affiliateUrl,
    category,
    consentGate,
    dogName,
    dogProfile,
    productId,
    productTitle,
    provider,
    requestDogId,
    subcategory,
  ])

  if (variant === 'compact') {
    // Free: hero preview + one SKU benefits entry. Pack Leader: hero + SKU analysis entry.
    if (!isHero && !isPackLeader && !isSkuCard) {
      return null
    }

    const confidenceTier =
      effectiveReason &&
      effectiveScore !== undefined &&
      effectiveScore !== null &&
      effectiveScore >= 80 &&
      effectiveScore < 100
        ? getConfidenceTier(effectiveScore)
        : null

    const handleToggle = (e?: React.SyntheticEvent) => {
      e?.preventDefault?.()
      e?.stopPropagation?.()
      if (!isPackLeader) {
        // Free: benefits entry → existing upgrade flow (never analysis/error).
        if (ctaPending) return
        openUpgradeModal({
          source: 'picks',
          feature: 'ai_picks',
          valueProposition:
            "Unlock personalized AI Fit analysis for every product. See why each pick matches your dog's breed, size, age, and current conditions.",
        })
        return
      }
      const next = !expanded
      setExpanded(next)
      if (next && isSkuCard && !effectiveReason && !loading) {
        void fetchSkuAnalysis()
      }
    }

    const handleRetry = (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      void fetchSkuAnalysis()
    }

    const nestedStyle = isV2
      ? picksV2NestedPlaceCardStyle
      : {
          backgroundColor: `${theme.textColor}08`,
          borderColor: `${theme.textColor}20`,
        }

    // Collapsed subtitle: benefits (Free) or open-analysis invite (Pack Leader).
    // Never show unavailable / limit marketing here (removes duplicate unavailable cards).
    const collapsedSubtitle = !isPackLeader
      ? isSkuCard
        ? AI_FIT_SKU_BENEFITS_COPY
        : 'Unlock why these products match your dog'
      : effectiveReason
        ? AI_FIT_SKU_OPEN_ANALYSIS_COPY
        : isSkuCard
          ? AI_FIT_SKU_OPEN_ANALYSIS_COPY
          : AI_FIT_SKU_OPEN_ANALYSIS_COPY

    return (
      <div
        className={`mt-2 ${className}`.trim()}
        data-ai-fit-compact="true"
        data-ai-fit-sku={isSkuCard ? 'true' : undefined}
        data-ai-fit-tier={isPackLeader ? 'pack_leader' : 'free'}
      >
        <button
          type="button"
          onClick={handleToggle}
          disabled={!isPackLeader && ctaPending}
          className="relative z-10 w-full rounded-xl border px-3 py-2.5 text-left transition-opacity hover:opacity-95 disabled:opacity-70"
          style={{ ...nestedStyle, touchAction: 'manipulation' }}
          data-ai-fit-entry="true"
          data-ai-fit-benefits={!isPackLeader ? 'true' : undefined}
          data-ai-fit-cta-pending={!isPackLeader && ctaPending ? 'true' : undefined}
          data-tap-probe={!isPackLeader ? 'ai-fit-benefits' : undefined}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-start gap-2">
              <Sparkles
                className="mt-0.5 h-4 w-4 shrink-0"
                style={{ color: isV2 ? picksV2Type.eyebrow.color : theme.textColor, opacity: 0.9 }}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <p
                    className="text-xs font-semibold leading-snug"
                    style={isV2 ? picksV2Type.headline : { color: theme.textColor }}
                  >
                    Personalized AI Fit
                  </p>
                  {!isPackLeader ? (
                    <span
                      className="text-[9px] font-semibold uppercase tracking-[0.12em]"
                      style={isV2 ? picksV2Type.eyebrow : { color: theme.textColor, opacity: 0.75 }}
                    >
                      Pack Leader
                    </span>
                  ) : null}
                </div>
                <p
                  className="text-[11px] leading-snug mt-0.5"
                  style={isV2 ? picksV2Type.secondary : { color: theme.textColor, opacity: 0.8 }}
                  data-ai-fit-collapsed-copy="true"
                >
                  {collapsedSubtitle}
                </p>
              </div>
            </div>
            {isPackLeader && expanded ? (
              <ChevronUp className="h-4 w-4 shrink-0 opacity-75" aria-hidden />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 opacity-75" aria-hidden />
            )}
          </div>
        </button>

        {isPackLeader && expanded ? (
          <div
            className="mt-2 rounded-xl border px-3 py-2.5"
            style={isV2 ? picksV2InfoPanelStyle : nestedStyle}
            data-ai-fit-panel="true"
            data-ai-fit-state={
              loading
                ? 'loading'
                : effectiveReason
                  ? 'analysis'
                  : error
                    ? 'error'
                    : 'idle'
            }
          >
            {loading ? (
              <p
                className="text-xs leading-relaxed"
                style={isV2 ? picksV2Type.body : { color: theme.textColor, opacity: 0.9 }}
              >
                Loading analysis…
              </p>
            ) : effectiveReason ? (
              <>
                <ul className="space-y-1" data-ai-fit-analysis="true">
                  {effectiveReason
                    .split(/[.!?]+/)
                    .filter((s) => s.trim().length > 10)
                    .slice(0, 4)
                    .map((point, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-1.5 text-xs leading-relaxed"
                        style={isV2 ? picksV2Type.body : { color: theme.textColor, opacity: 0.9 }}
                      >
                        <span className="mt-0.5">•</span>
                        <span>
                          {point.trim()}
                          {point.trim().endsWith('.') ? '' : '.'}
                        </span>
                      </li>
                    ))}
                </ul>
                {confidenceTier ? (
                  <p
                    className="text-[10px] mt-2 font-medium"
                    style={isV2 ? picksV2Type.metadata : { color: theme.textColor, opacity: 0.7 }}
                  >
                    {confidenceTier}
                  </p>
                ) : null}
              </>
            ) : (
              <div data-ai-fit-error="true" className="space-y-2">
                <p
                  className="text-xs leading-relaxed"
                  style={isV2 ? picksV2Type.body : { color: theme.textColor, opacity: 0.9 }}
                >
                  {error || AI_FIT_SKU_ERROR_COPY}
                </p>
                {isSkuCard ? (
                  <button
                    type="button"
                    onClick={handleRetry}
                    className="text-xs font-semibold underline underline-offset-2"
                    style={isV2 ? picksV2Type.headline : { color: theme.textColor }}
                    data-ai-fit-retry="true"
                  >
                    Retry
                  </button>
                ) : null}
              </div>
            )}
          </div>
        ) : null}
      </div>
    )
  }

  // --- Legacy full block (non-compact) ---
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.log('[AIFitBlock] Eligibility check:', {
      isHero,
      isPackLeader,
      matchScore,
      category,
      subcategory,
      bundleTitle,
      hasDogProfile: !!dogProfile,
      dogSignals: dogProfile ? {
        size: dogProfile.size,
        ageYears: dogProfile.ageYears,
        ageBracket: dogProfile.ageBracket,
        breed: dogProfile.breed,
      } : null,
    })
  }
  
  // CRITICAL: Only render on Hero Cards for FREE users
  // Pack Leader users should see AI Fit on ALL cards (hero + mini)
  if (!isHero && !isPackLeader) {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.log('[AIFitBlock] Not rendering: not a hero card and not Pack Leader')
    }
    return null
  }
  
  // CRITICAL: For FREE users, show AI Fit Preview on ALL hero cards (not just fit-relevant)
  // This ensures users see the value proposition on every bundle
  // For Pack Leader: Show on ALL cards (hero + mini) regardless of category relevance
  // Pack Leader subscribers must see AI Fit on ALL bundles, not just fit-relevant ones
  if (!isPackLeader) {
    // FREE users: Show preview on all hero cards regardless of category
    // Skip the category relevance check for hero cards
  } else {
    // Pack Leader: Show on ALL cards (hero + mini) - NO category relevance check
    // This ensures Pack Leader subscribers see AI Fit analysis on all bundles (Beds, Toys, Food, etc.)
    // Category relevance check removed - Pack Leader gets AI Fit everywhere
  }
  
  // FREE users: Show preview on hero cards if category is fit-relevant
  // Hero cards represent bundles and may not have individual product scores
  // We show AI Fit Preview to indicate the bundle has fit-relevant products
  if (!isPackLeader) {
    // Hero cards: Show if category is fit-relevant (no score requirement for hero cards)
    // If a score exists and is too low, still don't show (product-level check)
    if (matchScore !== undefined && matchScore !== null && (matchScore < 70 || matchScore >= 100)) {
      if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
        console.log('[AIFitBlock] Not rendering: FREE tier score requirement not met', { matchScore, required: '>= 70' })
      }
      return null
    }
    // If matchScore is undefined (typical for hero cards), show preview based on category relevance
  } else {
    // Pack Leader: hero keeps score/dog-signal quality bar; carousel cards unlock from tier alone
    if (isHero) {
      const scoreThreshold = 80
      if (matchScore === undefined || matchScore === null || matchScore < scoreThreshold || matchScore >= 100) {
        if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
          console.log('[AIFitBlock] Not rendering: Pack Leader score requirement not met', { matchScore, required: `>= ${scoreThreshold}`, isHero })
        }
        return null
      }

      if (!hasRequiredDogSignals(dogProfile)) {
        if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
          console.log('[AIFitBlock] Not rendering: Pack Leader missing dog signals')
        }
        return null
      }
    }
    // Mini/carousel cards: effective Pack Leader tier unlocks AI Fit (matches badge)
  }
  
  // DEBUG: Log successful render
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.log('[AIFitBlock] ✅ Rendering AI Fit block', {
      isPackLeader,
      matchScore,
      category,
      subcategory,
      bundleTitle,
    })
  }
  
  if (isPackLeader) {
    // Pack Leader: Personalized AI Fit
    const confidenceTier = matchScore !== undefined && matchScore !== null && matchScore >= 80 && matchScore < 100
      ? getConfidenceTier(matchScore)
      : null
    
    return (
      <div 
        className={`mt-3 px-3 py-2.5 rounded-lg border backdrop-blur-sm ${className}`}
        style={
          isV2
            ? picksV2InfoPanelStyle
            : {
                backgroundColor: `${theme.textColor}08`,
                borderColor: `${theme.textColor}20`,
              }
        }
      >
        <div className="flex items-start gap-2">
          <span className="text-base">🐾</span>
          <div className="flex-1">
            <div className="mb-2">
              <p 
                className="text-xs font-semibold"
                style={isV2 ? picksV2Type.primary : { color: theme.textColor }}
              >
                Your Dog's AI Fit
              </p>
            </div>
            
            {/* 2-4 bullet points from suitabilityReason (AI-generated) */}
            {suitabilityReason ? (
              <ul className="space-y-1 mb-2">
                {suitabilityReason
                  .split(/[.!?]+/)
                  .filter(s => s.trim().length > 10)
                  .slice(0, 4)
                  .map((point, idx) => (
                    <li 
                      key={idx}
                      className="text-xs leading-relaxed flex items-start gap-1.5"
                      style={isV2 ? picksV2Type.body : { color: theme.textColor, opacity: 0.9 }}
                    >
                      <span className="mt-0.5">•</span>
                      <span>{point.trim()}{point.trim().endsWith('.') ? '' : '.'}</span>
                    </li>
                  ))}
              </ul>
            ) : (
              // Fallback if no suitabilityReason
              <p 
                className="text-xs leading-relaxed mb-2"
                style={isV2 ? picksV2Type.body : { color: theme.textColor, opacity: 0.9 }}
              >
                Personalized fit analysis based on your dog's profile and walking needs.
              </p>
            )}
            
            {/* Confidence tier label (no numeric score) */}
            {confidenceTier && (
              <p 
                className="text-[10px] mt-2 font-medium"
                style={isV2 ? picksV2Type.metadata : { color: theme.textColor, opacity: 0.7 }}
              >
                {confidenceTier}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  // FREE: Complete preview showing all AI Fit features
  const categoryCopy = getFreeTierCopy(category, subcategory, bundleTitle)
  
  return (
    <div 
      className={`mt-3 relative rounded-lg overflow-hidden border backdrop-blur-sm ${className}`}
      style={
        isV2
          ? picksV2InfoPanelStyle
          : {
              borderColor: 'rgba(255, 255, 255, 0.3)',
              background: 'rgba(255, 255, 255, 0.65)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            }
      }
    >
      {/* Preview Content */}
      <div className="px-3 py-2.5">
        {/* Compact header: "AI Fit · Preview" */}
        <div className="mb-2">
          <p 
            className="text-xs font-semibold"
            style={isV2 ? picksV2Type.primary : { color: '#111827' }}
          >
            AI Fit
          </p>
        </div>
        
        {/* Category-aware static copy (1-2 sentences, editorial) */}
        <p 
          className="text-xs leading-relaxed mb-3"
          style={isV2 ? picksV2Type.body : { color: '#1F2937' }}
        >
          {categoryCopy}
        </p>
        
        {/* Show all AI Fit features that Pack Leader unlocks */}
        <div className="space-y-1.5 mb-3">
          <p 
            className="text-[10px] font-medium mb-1.5"
            style={isV2 ? picksV2Type.secondary : { color: '#374151' }}
          >
            Pack Leader AI Fit includes:
          </p>
          <div className="space-y-1">
            <div className="flex items-start gap-1.5">
              <span
                className="text-[10px] mt-0.5"
                style={isV2 ? picksV2Type.metadata : { color: '#4B5563' }}
              >
                •
              </span>
              <p 
                className="text-[10px] leading-relaxed"
                style={isV2 ? picksV2Type.body : { color: '#1F2937' }}
              >
                Personalized fit reasoning based on your dog's size, breed, and activity level
              </p>
            </div>
            <div className="flex items-start gap-1.5">
              <span
                className="text-[10px] mt-0.5"
                style={isV2 ? picksV2Type.metadata : { color: '#4B5563' }}
              >
                •
              </span>
              <p 
                className="text-[10px] leading-relaxed"
                style={isV2 ? picksV2Type.body : { color: '#1F2937' }}
              >
                Walk-specific guidance adjusted for current weather and conditions
              </p>
            </div>
            <div className="flex items-start gap-1.5">
              <span
                className="text-[10px] mt-0.5"
                style={isV2 ? picksV2Type.metadata : { color: '#4B5563' }}
              >
                •
              </span>
              <p 
                className="text-[10px] leading-relaxed"
                style={isV2 ? picksV2Type.body : { color: '#1F2937' }}
              >
                Confidence tier labels (Excellent Fit / Strong Fit) for each product
              </p>
            </div>
            <div className="flex items-start gap-1.5">
              <span
                className="text-[10px] mt-0.5"
                style={isV2 ? picksV2Type.metadata : { color: '#4B5563' }}
              >
                •
              </span>
              <p 
                className="text-[10px] leading-relaxed"
                style={isV2 ? picksV2Type.body : { color: '#1F2937' }}
              >
                Detailed bullet points explaining why each product fits your dog
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Refined Premium CTA Button */}
      <div 
        className="px-3 pb-2.5 pt-1 border-t"
        style={{
          borderColor: isV2 ? 'rgba(255, 236, 210, 0.22)' : '#E5E7EB',
        }}
      >
        <button
          type="button"
          disabled={ctaPending}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (ctaPending) return
            openUpgradeModal({
              source: 'picks',
              feature: 'ai_picks',
              valueProposition: 'Unlock personalized AI Fit analysis for every product. See why each pick matches your dog\'s breed, size, age, and current conditions.',
            })
          }}
          className="group relative w-full px-3 py-2 rounded-lg font-medium text-xs transition-all duration-200 hover:opacity-90 disabled:opacity-70"
          data-ai-fit-cta-pending={ctaPending ? 'true' : undefined}
          style={
            isV2
              ? {
                  background: picksV2CtaPrimary.background,
                  color: picksV2CtaPrimary.color,
                  border: `1px solid ${picksV2CtaPrimary.border}`,
                  boxShadow: picksV2CtaPrimary.boxShadow,
                }
              : {
                  background: '#111827',
                  color: '#FFFFFF',
                  border: '1px solid #1F2937',
                }
          }
        >
          <span className="relative flex items-center justify-center gap-1.5">
            <Lock className="h-3 w-3" />
            <span>View personalized AI Fit</span>
          </span>
        </button>
        
        {/* Secondary microcopy */}
        <p 
          className="text-[10px] mt-1.5 text-center"
          style={isV2 ? picksV2Type.metadata : { color: '#6B7280' }}
        >
          Available with Pack Leader
        </p>
      </div>
    </div>
  )
}
